import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Phone, MapPin, Briefcase, Hash, Calendar } from 'lucide-react';
import adhesionsService, { Adhesion } from '../../services/adhesions.service';
import { toast } from 'react-toastify';

const AdherentProfile: React.FC = () => {
    const [adhesion, setAdhesion] = useState<Adhesion | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await adhesionsService.getMyAdhesion();
                if (response?.data) {
                    setAdhesion(response.data);
                }
            } catch (err: any) {
                console.error('Error fetching profile:', err);
                toast.error('Erreur lors de la récupération du profil');
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, []);

    if (loading) {
        return (
            <div className="flex h-full items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary"></div>
            </div>
        );
    }

    if (!adhesion) {
        return (
            <div className="flex h-full items-center justify-center text-gray-500 min-h-[400px]">
                Profil introuvable.
            </div>
        );
    }

    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.4, staggerChildren: 0.1 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0 }
    };

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6 max-w-4xl mx-auto"
        >
            <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)] flex items-center gap-4">
                <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center text-2xl font-bold">
                    {adhesion.prenom.charAt(0)}{adhesion.nom.charAt(0)}
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Mon Profil</h1>
                    <p className="text-gray-500">Gérez vos informations personnelles</p>
                </div>
            </div>

            <motion.div variants={itemVariants} className="bg-white rounded-2xl shadow-[0_4px_20px_rgb(0,0,0,0.03)] overflow-hidden">
                <div className="px-8 py-5 border-b border-gray-50/50 flex items-center">
                    <h2 className="font-semibold text-lg text-gray-800 flex items-center gap-2">
                        <User className="w-5 h-5 text-secondary" />
                        Informations Personnelles
                    </h2>
                </div>
                
                <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                        <label className="text-xs font-semibold text-gray-400 tracking-wider uppercase">Nom complet</label>
                        <div className="flex items-center text-gray-900 bg-white shadow-[inset_0_2px_4px_rgba(0,0,0,0.02)] ring-1 ring-gray-100/50 p-3 rounded-xl">
                            <User className="w-4 h-4 mr-3 text-secondary/70" />
                            {adhesion.prenom} {adhesion.nom}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-xs font-semibold text-gray-400 tracking-wider uppercase">ID Membre</label>
                        <div className="flex items-center text-gray-900 bg-emerald-50/20 shadow-[inset_0_2px_4px_rgba(0,0,0,0.02)] ring-1 ring-emerald-100/30 p-3 rounded-xl">
                            <Hash className="w-4 h-4 mr-3 text-emerald-600/70" />
                            <span className="font-mono text-emerald-700 font-bold tracking-wide">
                                {adhesion.numeroMembre || 'En attente'}
                            </span>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-xs font-semibold text-gray-400 tracking-wider uppercase">Email</label>
                        <div className="flex items-center text-gray-900 bg-white shadow-[inset_0_2px_4px_rgba(0,0,0,0.02)] ring-1 ring-gray-100/50 p-3 rounded-xl">
                            <Mail className="w-4 h-4 mr-3 text-secondary/70" />
                            {adhesion.email}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-xs font-semibold text-gray-400 tracking-wider uppercase">Téléphone</label>
                        <div className="flex items-center text-gray-900 bg-white shadow-[inset_0_2px_4px_rgba(0,0,0,0.02)] ring-1 ring-gray-100/50 p-3 rounded-xl">
                            <Phone className="w-4 h-4 mr-3 text-secondary/70" />
                            {adhesion.telephone}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-xs font-semibold text-gray-400 tracking-wider uppercase">Date de naissance</label>
                        <div className="flex items-center text-gray-900 bg-white shadow-[inset_0_2px_4px_rgba(0,0,0,0.02)] ring-1 ring-gray-100/50 p-3 rounded-xl">
                            <Calendar className="w-4 h-4 mr-3 text-secondary/70" />
                            {new Date(adhesion.dateNaissance).toLocaleDateString('fr-FR')}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-xs font-semibold text-gray-400 tracking-wider uppercase">Secteur d'activité</label>
                        <div className="flex items-center text-gray-900 bg-white shadow-[inset_0_2px_4px_rgba(0,0,0,0.02)] ring-1 ring-gray-100/50 p-3 rounded-xl">
                            <Briefcase className="w-4 h-4 mr-3 text-secondary/70" />
                            {adhesion.secteur}
                        </div>
                    </div>

                    <div className="space-y-2 md:col-span-2">
                        <label className="text-xs font-semibold text-gray-400 tracking-wider uppercase">Adresse Complete</label>
                        <div className="flex items-start text-gray-900 bg-white shadow-[inset_0_2px_4px_rgba(0,0,0,0.02)] ring-1 ring-gray-100/50 p-3 rounded-xl">
                            <MapPin className="w-4 h-4 mr-3 mt-1 min-w-4 text-secondary/70" />
                            <div>
                                <p>{adhesion.adresse}</p>
                                <p className="text-sm text-gray-400 mt-1">Wilaya : {adhesion.wilaya}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default AdherentProfile;
